import { Injectable, inject } from '@angular/core';
import { AuthService } from './auth.service';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { ToastService } from '@infinitform/shared-ui';
import { GtmService } from '../gtm.service';

@Injectable({
  providedIn: 'root'
})
export class AppInitializationService {
  private authService = inject(AuthService);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private gtmService = inject(GtmService);


  async initializeApp(): Promise<void> {
    // Handle OAuth callback first
    await this.handleOAuthCallback();
    
    try {
      // Always try to refresh token if we have user data, regardless of current token state
      // This ensures we have a fresh token after page refresh
      if (this.authService.currentUser) {
        // This will now use the shared observable, preventing duplicate requests
        const response = await firstValueFrom(this.authService.refreshToken());
      }
    } catch (error) {
      // Clear invalid user data if refresh fails
      this.authService.clearTokens();
    }
  }

  private async handleOAuthCallback(): Promise<void> {
    const urlParams = new URLSearchParams(window.location.search);
    const authSuccess = urlParams.get('auth');
    const twoFactorRequired = urlParams.get('2fa');
    const userId = urlParams.get('userId');

    if (authSuccess === 'success') {
      // OAuth authentication successful - call refreshToken to set up user data
      try {
        const response = await firstValueFrom(this.authService.refreshToken());
        // refreshToken already handles localStorage setup

        const pendingSignup = sessionStorage.getItem('gtm_pending_signup');
        if (pendingSignup) {
          this.gtmService.push({ event: 'trialreg' });
          sessionStorage.removeItem('gtm_pending_signup');
        }

        this.toastService.show({
          type: 'success',
          text: 'Successfully logged in with Google!'
        });

        // Clear URL parameters and navigate
        this.router.navigate(['/user/projects'], { replaceUrl: true });
      } catch (error) {
        this.toastService.show({
          type: 'error',
          text: 'Authentication failed. Please try again.'
        });
        this.router.navigate(['/login'], { replaceUrl: true });
      }
    } else if (twoFactorRequired === 'required' && userId) {
      // OAuth user needs 2FA
      this.toastService.show({
        type: 'info',
        text: 'Please complete two-factor authentication to continue'
      });
      
      // Redirect to login with 2FA required
      this.router.navigate(['/login'], { 
        queryParams: { '2fa': 'required', 'userId': userId },
        replaceUrl: true 
      });
    }
  }
}
